import React from 'react';
import { Box, keyframes, useColorModeValue } from '@chakra-ui/react';
import { useAppearance } from '../../theme/AppearanceProvider';

// A slow breathing drift so the header field feels alive without distracting.
const float = (x: string) => keyframes`
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(${x}, 6%, 0) scale(1.12); }
  100% { transform: translate3d(0, 0, 0) scale(1); }
`;

// The "super amazing top" (Chad): an ambient brand-coloured aurora anchored to the
// TOP of the content column so the KPI strip floats on a glowing brand field instead
// of dead black. Brand-token blobs (so it tracks each company's accent — red here),
// masked to fade down into the page. Aurora-skin only; calm under reduced motion.
const HeroGlow: React.FC = () => {
  // item 330 — `motion` is false under OS reduced-motion AND in reduced-effects mode:
  // animating a scale on two half-viewport blur(110px) blobs re-rasterizes the blur
  // every frame, forever — the single biggest GPU drain on slow PCs. Static blobs
  // raster ONCE and are then free.
  const { aurora, motion } = useAppearance();
  const reduceMotion = !motion;
  // item 155 — capped (was 0.4/0.48) so a SATURATED brand accent (e.g. red) accents
  // the top field instead of blanketing it as a heavy wash. Still glows at green.
  const opacity = useColorModeValue(0.33, 0.4);
  const green = useColorModeValue('var(--chakra-colors-brandGreen-300)', 'var(--chakra-colors-brandGreen-400)');
  const blue = useColorModeValue('var(--chakra-colors-brandBlue-300)', 'var(--chakra-colors-brandBlue-500)');

  if (!aurora) return null;
  // item 732 (Chad) — TEMP experiment: hide the top hero glow to preview a flat look. Flip to
  // false to bring it back.
  const HIDE_GRADIENT = true;
  if (HIDE_GRADIENT) return null;

  return (
    <Box
      aria-hidden
      position="absolute"
      top={0}
      // stay strictly inside the content column (left:0 = the content edge, right at
      // the sidebar boundary) so the blurred blobs can't bleed behind the translucent
      // sidebar and tint it; overflow:hidden clips them to this box.
      left={0}
      right={0}
      h={{ base: '420px', md: '560px' }}
      overflow="hidden"
      pointerEvents="none"
      zIndex={0}
      // radial fade that reaches FULLY transparent well inside the column (horiz
      // radius < distance to the edge) so there's no glow left at the clip boundary
      // — kills the hard vertical cut by the sidebar gutter (Chad: "still a hard line")
      sx={{
        maskImage: 'radial-gradient(85% 75% at 50% -12%, #000 25%, transparent 78%)',
        WebkitMaskImage: 'radial-gradient(85% 75% at 50% -12%, #000 25%, transparent 78%)',
      }}
    >
      <Box
        position="absolute"
        top="-55%"
        left="6%"
        w="58%"
        h="150%"
        bg={green}
        opacity={opacity}
        filter="blur(110px)"
        borderRadius="full"
        willChange="transform"
        animation={reduceMotion ? undefined : `${float('12%')} 30s ease-in-out infinite`}
      />
      <Box
        position="absolute"
        top="-60%"
        right="2%"
        w="52%"
        h="150%"
        bg={blue}
        opacity={opacity}
        filter="blur(110px)"
        borderRadius="full"
        willChange="transform"
        animation={reduceMotion ? undefined : `${float('-12%')} 38s ease-in-out infinite`}
      />
    </Box>
  );
};

export default HeroGlow;
